import PropTypes from 'prop-types'
import { memo, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Fade from 'components/animation/Fade'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { noop } from 'utils'
import Transition from 'components/animation/Transition'
import Center from 'components/Center'
import classNames from 'classnames'

export const Deem = ({ children, on, duration }) => {
  const [popupVisible, setPopupVisible] = useState(false)
  useEffect(() => {
    if (on) setPopupVisible(true)
  }, [on])
  return createPortal(
    <>
      <Fade
        on={on}
        duration={duration}
        style={{
          position: 'fixed',
          zIndex: 1100,
        }}
        onFadeOut={() => setPopupVisible(false)}>
        <div className="dimmed_layer" />
      </Fade>
      {popupVisible && <>{children}</>}
    </>,
    window.document.body
  )
}

export const DeemedPopup = memo(
  ({
    on,
    className,
    style = {},
    onBackgroundClick = noop,
    onOpen = noop,
    changeLayoutClass = true,
    children,
  }) => {
    const ref = useRef()
    useEffect(() => {
      if (changeLayoutClass) {
        if (on) {
          document.body.classList.add('layer_on')
        } else {
          document.body.classList.remove('layer_on')
        }
      }
      return () => {
        if (changeLayoutClass) {
          document.body.classList.remove('layer_on')
        }
      }
    }, [on, changeLayoutClass])
    return (
      <Deem on={on} duration={300}>
        <Center.Container
          tag="div"
          className="layer_wrap"
          style={{
            position: 'fixed',
            zIndex: 1100,
          }}
          ref={ref}
          onClick={e => {
            if (!ref.current.contains(e.target) || ref.current === e.target) {
              onBackgroundClick()
            }
          }}>
          <Center offsetY={-60} style={{ perspective: 1000 }}>
            <Transition
              on={on}
              style={style}
              className={className}
              transitionIn={100}
              transitionOut={200}
              onTransitionIn={onOpen}
              cssProperties={{
                opacity: [0, 1],
                transform: ['translateY(-70px) scale(.95) rotateX(15deg)', ''],
              }}>
              {children}
            </Transition>
          </Center>
        </Center.Container>
      </Deem>
    )
  }
)

DeemedPopup.displayName = 'DeemedPopup'
DeemedPopup.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  on: PropTypes.bool,
  onBackgroundClick: PropTypes.func,
  onOpen: PropTypes.func,
  style: PropTypes.object,
  changeLayoutClass: PropTypes.bool,
}

export const Modal = ({
  on,
  type = 'basic_layer',
  className,
  innerClassName,
  onBackgroundClick,
  style,
  children,
}) => (
  <DeemedPopup
    className={classNames(type, className)}
    on={on}
    onBackgroundClick={onBackgroundClick}
    style={{ marginTop: 60, ...style }}>
    <div className={classNames('inner_basic_layer', innerClassName)}>
      {children}
    </div>
  </DeemedPopup>
)

export const ModalRoute = ({
  path,
  type = 'basic_layer',
  className,
  innerClassName,
  children,
  closeOnBackgroundClick = false,
}) => {
  const currentMatch = useRouteMatch()
  const isModalMatch = !!useRouteMatch(path)
  const history = useHistory()
  const [modalOn, setModalOn] = useState(false)

  useEffect(() => {
    setModalOn(isModalMatch)
  }, [isModalMatch])

  return (
    <Modal
      on={modalOn}
      className={className}
      innerClassName={innerClassName}
      type={type}
      style={{
        position: 'relative',
        left: 'auto',
        top: 'auto',
      }}
      onBackgroundClick={() =>
        closeOnBackgroundClick && history.push(currentMatch.url)
      }>
      {children}
    </Modal>
  )
}

Deem.propTypes = {
  children: PropTypes.node,
  on: PropTypes.bool,
  onClick: PropTypes.func,
}

Modal.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  innerClassName: PropTypes.string,
  on: PropTypes.bool,
  onBackgroundClick: PropTypes.func,
  style: PropTypes.object,
  type: PropTypes.string,
}

ModalRoute.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  closeOnBackgroundClick: PropTypes.bool,
  innerClassName: PropTypes.any,
  path: PropTypes.string.isRequired,
  type: PropTypes.string,
}
